import { ReactNode } from 'react'

import { ClassNames, css, useTheme } from '@emotion/react'
import { ClassNames as DayPickerClassNames } from 'react-day-picker'

import { MooncakeTheme, spacing } from '../../theme'
import { useDropdownStatus } from './DatePickerDropdownContext'

type Props = {
  children: (classNames: DayPickerClassNames) => ReactNode
}

export const buttonLikeBase = (theme: MooncakeTheme) => css`
  ${theme.typography.body};
  color: ${theme.colors.label};
  background: ${theme.colors.secondaryButtonBackground};
  border-radius: 0.5rem;
  border: 2px solid transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  outline: 0;
`

export const buttonLikeActive = (theme: MooncakeTheme) => css`
  background: ${theme.colors.secondaryButtonActive};
`

export const buttonLikeSelected = (theme: MooncakeTheme) => css`
  border: 2px solid ${theme.colors.primary};
  border-radius: 0.5rem;
  font-weight: ${theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  background: none;
`

export const buttonLikeFocus = (theme: MooncakeTheme) => css`
  outline-style: solid;
  outline-width: 0.5rem;
  outline-color: ${theme.colors.primary}33;
`

const smallButtonMotion = (theme: MooncakeTheme) => css`
  ${theme.motion.standardTransition};
  transition-property: transform, opacity;
  transform-origin: center center;

  @media (hover: hover) {
    :hover {
      opacity: 0.75;
    }
  }

  :active {
    opacity: 0.5;
    ${theme.motion.standardSubtleActiveState};
  }
`

export const visuallyHidden = css`
  border: 0;
  clip: rect(0 0 0 0);
  height: auto;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`

export const MooncakeDatePickerClassNames = ({ children }: Props) => {
  const theme = useTheme()
  const dropdownStatus = useDropdownStatus()

  const dayInnerSize = '1.5rem'
  const daySize = '2.5rem'

  return (
    <ClassNames>
      {({ css }) =>
        children({
          button: css``,
          day: css`
            ${buttonLikeBase(theme)}
            background: none;
            position: absolute;
            width: ${daySize};
            height: ${daySize};

            ${theme.selectors.keyboard} &:focus {
              ${buttonLikeFocus(theme)}
            }

            ${smallButtonMotion(theme)}

            :disabled {
              color: ${theme.colors.disabledLabel};
              cursor: default;
              transition: none;
            }
          `,
          day_selected: css`
            /* increase specificity to always be greater than above "day" */
            body & {
              ${buttonLikeSelected(theme)}
              color: ${theme.colors.primary};
              transition-property: transform;
            }
          `,
          day_today: css`
            font-weight: ${theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
          `,
          head_cell: css`
            ${theme.typography.identifier};
            text-align: center;
            vertical-align: top;
            color: ${theme.colors.placeholderLabel};
            width: ${dayInnerSize};
          `,
          head: css`
            display: grid;
          `,
          head_row: css`
            display: flex;
            justify-content: space-between;
          `,
          tbody: css`
            display: grid;
            gap: ${spacing.m};
          `,
          cell: css`
            position: relative;
            width: ${dayInnerSize};
            height: ${dayInnerSize};
            display: flex;
            align-items: center;
            justify-content: center;
          `,
          row: css`
            display: flex;
            justify-content: space-between;
          `,
          caption: css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: ${theme.spacing.s};
            align-items: center;
            justify-content: space-between;
            margin-bottom: ${theme.spacing.l};
          `,
          caption_label: css`
            ${visuallyHidden}
          `,
          caption_dropdowns: css`
            grid-column: span 3; /* align exactly with three dropdown options */
            width: 100%;
            display: flex;
          `,
          nav: css`
            display: flex;
            gap: calc(${theme.spacing.xxs} / 2);
            margin-left: calc(${theme.spacing.s} * -1); /* erase grid colum gap */
            padding-left: ${spacing.xxs};
            justify-content: space-between;
          `,
          nav_button: css`
            color: ${theme.colors.tertiaryIcon};
            background: none;
            border: none;
            padding: 0;
            width: 2rem;
            height: 2rem;
            cursor: pointer;

            ${smallButtonMotion(theme)}

            :disabled {
              color: ${theme.colors.disabledIcon};
              cursor: default;
              transition: none;
            }
          `,
          nav_button_next: css`
            rotate: 90deg;
          `,
          nav_button_previous: css`
            rotate: -90deg;
          `,
          table: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: ${spacing.m};

            ${dropdownStatus.open &&
            // hides table while preserving width
            css`
              display: grid;
              grid-auto-rows: 0;
              gap: 0;
              visibility: hidden;
              margin-bottom: 0;
            `}
          `,
        })
      }
    </ClassNames>
  )
}
